package com.trevorwiebe.timesheet.admin.locations.presentation

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuAnchorType
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.unit.dp
import com.trevorwiebe.timesheet.core.presentation.common.DialogButtonRow
import com.trevorwiebe.timesheet.core.presentation.common.NativeDialog
import com.trevorwiebe.timesheet.core.presentation.common.TimesheetTextField
import com.trevorwiebe.timesheet.core.presentation.common.responsiveDialogWidth
import com.trevorwiebe.timesheet.mileage.domain.model.LocationType

/**
 * Platform wrapper for the add/edit location form: full-screen dialog with a
 * close (X) top bar on Android, a card dialog on web.
 */
@Composable
expect fun AddEditLocationDialog(
    form: LocationFormState,
    isEditing: Boolean,
    isDeleted: Boolean,
    isGeocoding: Boolean,
    geocodeError: String?,
    onEvent: (LocationsEvent) -> Unit
)

@OptIn(ExperimentalMaterial3Api::class)
@Composable
internal fun AddEditLocationFormContent(
    form: LocationFormState,
    isEditing: Boolean,
    isDeleted: Boolean,
    isGeocoding: Boolean,
    geocodeError: String?,
    onEvent: (LocationsEvent) -> Unit
) {
    var typeDropdownExpanded by remember { mutableStateOf(false) }
    var showDeleteConfirm by remember { mutableStateOf(false) }

    NativeDialog(
        visible = showDeleteConfirm,
        title = "Delete Location",
        message = "Are you sure you want to delete this location? It can be restored, and existing mileage entries will keep their reference.",
        confirmText = "Delete",
        onConfirm = { onEvent(LocationsEvent.OnDeleteLocation) },
        dismissText = "Cancel",
        onDismiss = { showDeleteConfirm = false }
    )

    Column(
        modifier = Modifier
            .responsiveDialogWidth()
            .fillMaxWidth()
            .verticalScroll(rememberScrollState())
            .padding(horizontal = 16.dp)
    ) {
        Text(
            text = if (isEditing) "Edit Location" else "Add Location",
            style = MaterialTheme.typography.titleLarge
        )
        Spacer(modifier = Modifier.height(16.dp))

        TimesheetTextField(
            text = form.name,
            label = "Name",
            keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Words),
            onTextChange = { onEvent(LocationsEvent.OnFormNameChanged(it)) }
        )
        Spacer(modifier = Modifier.height(8.dp))

        TimesheetTextField(
            text = form.city,
            label = "City",
            keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Words),
            onTextChange = { onEvent(LocationsEvent.OnFormCityChanged(it)) }
        )
        Spacer(modifier = Modifier.height(8.dp))

        TimesheetTextField(
            text = form.state,
            label = "State",
            keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Words),
            onTextChange = { onEvent(LocationsEvent.OnFormStateChanged(it)) }
        )
        Spacer(modifier = Modifier.height(8.dp))

        TimesheetTextField(
            text = form.address,
            label = "Address",
            keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Words),
            onTextChange = { onEvent(LocationsEvent.OnFormAddressChanged(it)) }
        )
        Spacer(modifier = Modifier.height(8.dp))

        LocationCoordinateControls(
            form = form,
            isGeocoding = isGeocoding,
            geocodeError = geocodeError,
            onEvent = onEvent
        )
        Spacer(modifier = Modifier.height(8.dp))

        ExposedDropdownMenuBox(
            expanded = typeDropdownExpanded,
            onExpandedChange = { typeDropdownExpanded = it }
        ) {
            TimesheetTextField(
                modifier = Modifier.menuAnchor(ExposedDropdownMenuAnchorType.PrimaryNotEditable),
                text = form.type.name.lowercase().replaceFirstChar { it.uppercase() },
                label = "Type",
                readOnly = true,
                trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = typeDropdownExpanded) }
            )
            ExposedDropdownMenu(
                expanded = typeDropdownExpanded,
                onDismissRequest = { typeDropdownExpanded = false }
            ) {
                LocationType.entries.forEach { type ->
                    DropdownMenuItem(
                        text = { Text(type.name.lowercase().replaceFirstChar { it.uppercase() }) },
                        onClick = {
                            onEvent(LocationsEvent.OnFormTypeChanged(type))
                            typeDropdownExpanded = false
                        }
                    )
                }
            }
        }
        Spacer(modifier = Modifier.height(8.dp))

        form.validationError?.let { error ->
            Text(
                text = error,
                color = MaterialTheme.colorScheme.error,
                style = MaterialTheme.typography.bodySmall,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(bottom = 8.dp)
            )
        }

        DialogButtonRow(
            confirmButtonText = "Save",
            confirmEnabled = !isDeleted,
            onConfirmClick = { onEvent(LocationsEvent.OnSaveLocation) },
            dismissButtonText = "Cancel",
            onDismissClick = { onEvent(LocationsEvent.OnDismissDialog) },
            neutralButtonText = when {
                !isEditing -> null
                isDeleted -> "Restore"
                else -> "Delete"
            },
            onNeutralClick = if (isDeleted) {
                { onEvent(LocationsEvent.OnRestoreLocation) }
            } else {
                { showDeleteConfirm = true }
            }
        )
    }
}
